import useSWR, { type KeyedMutator } from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import type { PropertyInfoResponse } from "@/features/propertyCodeAcquisition/types";
import {
  buildPropertyInfoEndpoint,
  getPropertyInfo,
} from "@/features/propertyCodeAcquisition/api/getPropertyInfo";
import { PROPERTY_CODE_SWR_OPTIONS } from "@/features/propertyCodeAcquisition/hooks/propertyCodeSwrConfig";

interface UsePropertyInfoReturn {
  propertyInfo: PropertyInfoResponse | undefined;
  isLoading: boolean;
  error: Error | undefined;
  mutate: KeyedMutator<PropertyInfoResponse>;
}

/**
 * 物件情報（画面ヘッダー）を取得するカスタムフック。
 * GET /property-codes/{code}（apiClient の baseURL が /api を付与）。
 * code が null の場合は SWR の key を null にしてリクエストを発火させない（条件付きフェッチ）。
 * 404 等のエラーは error として返し、判定（HTTP ステータス）は呼び出し側で行う。
 * @param code 物件コード（UUID）。未確定時は null。
 */
export const usePropertyInfo = (code: string | null): UsePropertyInfoReturn => {
  const { apiClient } = useApiClient();

  const { data, error, isLoading, mutate } = useSWR<
    PropertyInfoResponse,
    Error
  >(
    code != null ? buildPropertyInfoEndpoint(code) : null,
    // key が非 null（code 非 null）の時のみ fetcher が呼ばれる。fetcher 自体を null/非 null で
    // 出し分けることで、true 枝で code が string に絞り込まれ as キャストが不要になる。
    code != null ? async () => await getPropertyInfo(apiClient, code) : null,
    PROPERTY_CODE_SWR_OPTIONS
  );

  return { propertyInfo: data, isLoading, error, mutate };
};
